import { currencyType } from "./types/currency";
import type { Citation } from "./citation-type";

export type { Citation } from "./citation-type";

// ---------------------------------------------------------------------------
// Adapter: wire → canonical
// ---------------------------------------------------------------------------

type WireRecord = Record<string, unknown>;

function asRecord(value: unknown): WireRecord | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as WireRecord;
  }
  return undefined;
}

function isCurrencyShape(
  v: unknown,
): v is { amount: number; currency_code?: string; currency?: string } {
  if (!v || typeof v !== "object") return false;
  const obj = v as { amount?: unknown; currency_code?: unknown; currency?: unknown };
  return (
    typeof obj.amount === "number" &&
    ((typeof obj.currency_code === "string" && obj.currency_code.length > 0) ||
      (typeof obj.currency === "string" && obj.currency.length > 0))
  );
}

function formatFromValueSnapshot(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (isCurrencyShape(value)) {
    // Delegate to the canonical currency registry entry so currency
    // formatting has exactly one Intl.NumberFormat call site.
    return currencyType.format({
      amount: value.amount,
      currency_code: value.currency_code ?? value.currency ?? "USD",
    });
  }
  if (typeof value === "string") return value.length > 0 ? value : undefined;
  if (typeof value === "number") return value.toLocaleString("en-US");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((x) => String(x)).join(", ");
  return undefined;
}

function pickPage(wire: WireRecord): number | undefined {
  const candidates = [wire.page, wire.pageNumber, wire.page_number];
  for (const c of candidates) {
    if (typeof c === "number" && Number.isFinite(c)) return c;
  }
  return undefined;
}

function pickBbox(wire: WireRecord): [number, number, number, number] | undefined {
  const raw = wire.bbox;
  if (raw == null) return undefined;
  if (Array.isArray(raw) && raw.length === 4 && raw.every((n) => typeof n === "number")) {
    return [raw[0] as number, raw[1] as number, raw[2] as number, raw[3] as number];
  }
  const obj = asRecord(raw);
  if (
    obj &&
    typeof obj.left === "number" &&
    typeof obj.top === "number" &&
    typeof obj.width === "number" &&
    typeof obj.height === "number"
  ) {
    return [obj.left, obj.top, obj.width, obj.height];
  }
  return undefined;
}

function pickDocumentId(wire: WireRecord): string {
  const directKeys = ["document_id", "fileId", "source_extraction_id", "sourceReferenceId"];
  for (const k of directKeys) {
    const v = wire[k];
    if (typeof v === "string" && v.length > 0) return v;
  }
  const file = asRecord(wire.file);
  if (file) {
    const fid = file.id;
    if (typeof fid === "string" && fid.length > 0) return fid;
  }
  return "";
}

function pickLabel(wire: WireRecord): string {
  if (typeof wire.label === "string" && wire.label.length > 0) return wire.label;
  const file = asRecord(wire.file);
  if (file && typeof file.file_name === "string" && file.file_name.length > 0) {
    return file.file_name;
  }
  if (typeof wire.filename === "string" && wire.filename.length > 0) return wire.filename;
  if (typeof wire.sourceLabel === "string" && wire.sourceLabel.length > 0) return wire.sourceLabel;
  const snapshot = wire.value_snapshot ?? wire.fieldValue;
  const formatted = formatFromValueSnapshot(snapshot);
  if (formatted !== undefined) return formatted;
  return "Citation";
}

function pickSnippet(wire: WireRecord): string | undefined {
  const candidates = [wire.snippet, wire.content, wire.citedText];
  for (const c of candidates) {
    if (typeof c === "string" && c.length > 0) return c;
  }
  return undefined;
}

/**
 * Normalize an unknown wire-shape citation into the canonical `Citation`.
 *
 * Throws when `id` is missing — every citation must have a stable id.
 * Other fields fall through a fallback chain that accepts the various
 * snake_case / camelCase wire shapes the API and worker emit.
 */
export function parseCitation(wire: unknown): Citation {
  const record = asRecord(wire);
  if (!record) {
    throw new Error("parseCitation: expected an object");
  }
  const id = record.id;
  if (typeof id !== "string" || id.length === 0) {
    throw new Error("parseCitation: missing id");
  }
  const out: Citation = {
    id,
    document_id: pickDocumentId(record),
    label: pickLabel(record),
  };
  const page = pickPage(record);
  if (page !== undefined) out.page = page;
  const bbox = pickBbox(record);
  if (bbox !== undefined) out.bbox = bbox;
  const snippet = pickSnippet(record);
  if (snippet !== undefined) out.snippet = snippet;
  return out;
}

export function parseCitations(wire: unknown[]): Citation[] {
  return wire.map(parseCitation);
}
